import React, {
    useCallback, useMemo,
} from 'react'
import clsx from 'clsx'
import noop from 'lodash/noop'
import {
    FormControlLabel,
    Checkbox as MaterialCheckbox,
} from '@mui/material'

import NotificationIcon from '../NotificationIcon'
import {
    NotificationIconProps,
} from '../NotificationIcon/notificationIcon.types'
import useStyles from './Checkbox.styles'

type Props = {
    title?: string
    disabled?: boolean
    indeterminate?: boolean
    onChange?: (value: boolean, event: { target: { name: string; value: boolean } }) => void
    name?: string
    value?: boolean
    className?: string
    notificationIconProps?: NotificationIconProps
}

const defaultProps: Props = {
    title: '',
    disabled: false,
    indeterminate: false,
    onChange: noop,
    name: '',
    value: false,
    className: '',
    notificationIconProps: undefined,
}

const Checkbox = ({
    title,
    disabled,
    indeterminate,
    onChange,
    name,
    value,
    className,
    notificationIconProps,
}: Readonly<Props>) => {
    const handleChange = useCallback(() => {
        const newValue = !value

        onChange(newValue, {
            target: {
                name,
                value: newValue,
            },
        })
    }, [
        name,
        onChange,
        value,
    ])

    const {
        classes,
    } = useStyles({
        disabled,
    })

    const LabelComponent = useMemo(() => {
        if (!notificationIconProps) {
            return title
        }
        return (
            <NotificationIcon
                content={notificationIconProps.content}
                type={notificationIconProps.type}
                tooltipPlacement={notificationIconProps.tooltipPlacement}
                isInlineIcon
            >
                {title}
            </NotificationIcon>
        )
    }, [
        title,
        notificationIconProps,
    ])

    return (
        <div className={clsx(className, classes.checkboxWrapper)}>
            <div>
                <div className={classes.requiredMark} />
                <FormControlLabel
                    control={
                        (
                            <MaterialCheckbox
                                data-testid={`checkbox-${name || title}`}
                                disabled={disabled}
                                checked={value}
                                color="primary"
                                indeterminate={indeterminate}
                                onChange={handleChange}
                                value={value}
                                name={name}
                                classes={{
                                    root: classes.checkboxRoot,
                                }}
                                slotProps={{
                                    input: {
                                        'data-testid': `checkbox-input-${name || title}`,
                                    } as React.InputHTMLAttributes<HTMLInputElement>,
                                }}

                            />
                        )
                    }
                    label={LabelComponent}
                />
            </div>
        </div>
    )
}

Checkbox.defaultProps = defaultProps

export default Checkbox
